import { useEffect, useState } from 'react';

import { TAB_GUARD_CHANNEL } from '../configs/connect';

// Single-leader election across browser tabs sharing one origin. The first
// tab to mount becomes the leader and runs the CCP iframe; subsequent tabs
// observe the leader's heartbeat and stay idle until it goes away.
//
// Implementation: localStorage holds `{ id, ts }` of the current leader.
// Leader bumps `ts` every HEARTBEAT_MS. Followers consider the lock stale
// after STALE_MS without a bump and try to take over. A `BroadcastChannel`
// notifies followers immediately when the leader releases the lock so they
// don't have to poll.

const LOCK_KEY = 'v4_connect_tab_leader';
const HEARTBEAT_MS = 3000;
const STALE_MS = 9000;

interface Lock {
  id: string;
  ts: number;
}

const readLock = (): Lock | null => {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as Lock).id === 'string' &&
      typeof (parsed as Lock).ts === 'number'
    ) {
      return parsed as Lock;
    }
    return null;
  } catch {
    return null;
  }
};

const writeLock = (lock: Lock): void => {
  try {
    localStorage.setItem(LOCK_KEY, JSON.stringify(lock));
  } catch {
    // Ignore — leadership stays unowned for this tab.
  }
};

const clearLock = (id: string): void => {
  const current = readLock();
  if (current?.id === id) {
    try {
      localStorage.removeItem(LOCK_KEY);
    } catch {
      /* ignore */
    }
  }
};

const isStale = (lock: Lock): boolean => Date.now() - lock.ts > STALE_MS;

/**
 * Returns true if the current tab holds leadership. Re-elects automatically
 * when the existing leader goes stale or sends a release notification.
 */
export const useTabLeader = (enabled: boolean): boolean => {
  const [isLeader, setIsLeader] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsLeader(false);
      return;
    }

    // crypto.randomUUID is universal in our supported browsers; the fallback
    // is paranoia for legacy contexts and uses crypto.getRandomValues so
    // there's no insecure-random in the codebase.
    const id =
      typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : Array.from(crypto.getRandomValues(new Uint8Array(16)))
            .map((b) => b.toString(16).padStart(2, '0'))
            .join('');
    let claimed = false;
    let heartbeatId: number | undefined;
    const channel =
      typeof BroadcastChannel === 'function' ? new BroadcastChannel(TAB_GUARD_CHANNEL) : null;

    const tryClaim = () => {
      const existing = readLock();
      if (!existing || isStale(existing) || existing.id === id) {
        writeLock({ id, ts: Date.now() });
        if (!claimed) {
          claimed = true;
          setIsLeader(true);
          heartbeatId = window.setInterval(() => writeLock({ id, ts: Date.now() }), HEARTBEAT_MS);
        }
      }
    };

    tryClaim();

    // Re-check periodically in case the leader's tab went away without
    // emitting a release (browser crash, hard close).
    const pollId = window.setInterval(tryClaim, HEARTBEAT_MS);

    if (channel) {
      channel.onmessage = (e: MessageEvent) => {
        if (e.data === 'released' && !claimed) tryClaim();
      };
    }

    return () => {
      if (heartbeatId) window.clearInterval(heartbeatId);
      window.clearInterval(pollId);
      if (claimed) {
        clearLock(id);
        channel?.postMessage('released');
      }
      channel?.close();
    };
  }, [enabled]);

  return isLeader;
};
